/**
 * Admin Home context header — greeting from real auth identity plus the
 * organization's name (an Admin can belong to more than one organization, so
 * the page has to say which one it is showing). The name comes from the
 * organization record; when it cannot be read it is simply left out — never a
 * placeholder, and the header carries no invented metrics or sample names.
 */
import { intentColors, typography } from "@/design";
import { cn } from "@/lib/utils";

export type AdminHomeContextHeaderProps = {
  firstName?: string | null;
  organizationName?: string | null;
};

export function AdminHomeContextHeader({
  firstName,
  organizationName,
}: AdminHomeContextHeaderProps) {
  const greetingName = firstName?.trim() || "Admin";
  const orgName = organizationName?.trim() || null;

  return (
    <header
      className={cn(
        "rounded-lg border border-slate-200 bg-white px-5 py-5 shadow-sm",
        intentColors.neutral.borderSubtle,
      )}
      data-testid="admin-home-context-header"
    >
      <div className="min-w-0 space-y-1.5">
        <p className={typography.label}>
          Admin Home
          {orgName ? (
            <>
              {" · "}
              <span data-testid="admin-home-org-name">{orgName}</span>
            </>
          ) : null}
        </p>
        <h1 className={typography.pageTitle} data-testid="admin-home-greeting">
          Welcome back, {greetingName}
        </h1>
        <p
          className={typography.body}
          data-testid="admin-home-context-subtitle"
        >
          Workspaces, setup, and governance from one place.
        </p>
      </div>
    </header>
  );
}
